/* eslint-disable jsx-a11y/alt-text */
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import NewCustomerModal from "src/components/modals/NewCustomerModal";
import Input from "src/components/widgets/Input";
import { IoCloudOfflineSharp } from "react-icons/io5";
import { FaMoneyBillTransfer } from "react-icons/fa6";
import {
	cn,
	getCustomizationValue,
	removeFromLocalStorage,
	renderCurrency,
	renderNumber,
	showErrorToast,
	showInfoToast,
	showSuccessToast,
} from "src/lib/utils";
import FormLabel from "src/components/FormLabel";
import ComboSearch from "src/components/ComboSearch";
import { FieldArray, Formik } from "formik";
import * as Yup from "yup";
import CustomModal from "src/components/CustomModal";
import { getCustomers } from "src/api/customer";
import { getInventory } from "src/api/inventory";
import CustomDataTable from "src/components/CustomDataTable";
import { Button } from "src/components/ui/button";
import { X } from "lucide-react";
import { useMutation } from "@tanstack/react-query";
import { createInvoice, getInvoiceGroups } from "src/api/invoice";
import { format } from "date-fns";
import BackIcon from "../images/back.png";
import { useDashboardContext } from "src/context/DashboardContext";
import { useAuth } from "src/providers/AuthProvider";
import AddProductModal from "src/components/modals/AddProductModal";

const initialValues = {
	customer: null,
	customer_id: "",
	group_id: "",
	invoice_items: [],
	discount: 0,
	due_date: format(new Date(), "yyy-MM-dd"),
	payment_method: "",
};
const validationSchema = Yup.object()
	.shape({
		customer_id: Yup.string(),
		group_id: Yup.string(),
		invoice_items: Yup.array()
			.of(
				Yup.object().shape({
					product_id: Yup.string().required("Product ID is required"),
					qty: Yup.number().required("Quantity is required").min(1, "Quantity must be at least 1"),
				}),
			)
			.min(1, cn("At least one invoice item is required")),
		discount: Yup.number().min(0, "Discount cannot be negative").max(100, "Discount cannot be more than 100%"),
		due_date: Yup.date().required("Due date is required"),
		payment_method: Yup.string().required("Payment method is required"),
	})
	.test("customer-or-group", "Either customer or group must be selected, not both", function (values) {
		const hasCustomer = !!values.customer_id;
		const hasGroup = !!values.group_id;
		// Only one of them should be present, not both or neither
		return (hasCustomer || hasGroup) && !(hasCustomer && hasGroup);
	});

const columns = (setFieldValue, remove) => [
	{ name: "Item", selector: (row) => row.product.name, grow: 2 },
	{ name: "Price", selector: (row) => renderCurrency(row.product.price) },
	{
		name: "Qty",
		cell: (row, index) => (
			<input
				type="number"
				value={row.qty}
				className="!tw-bg-transparent !tw-w-full"
				onChange={(e) => {
					const qty = e.target.valueAsNumber;
					if (qty > row.product.available_qty) {
						showErrorToast("Not enough inventory in stock");
						return;
					}
					setFieldValue(`invoice_items.${index}.qty`, qty);
				}}
			/>
		),
	},
	{ name: "Total", selector: (row) => renderCurrency(row.product.price * row.qty) },
	{
		name: "",
		cell: (_, index) => (
			<Button className="!tw-p-1" variant="destructive" onClick={() => remove(index)}>
				<X />
			</Button>
		),
		width: "80px",
	},
];

const AddInvoice = () => {
	const { user } = useAuth();
	const navigate = useNavigate();
	const { settings } = useDashboardContext();

	const paymentIcons = {
		transfer: <FaMoneyBillTransfer size={24} />,
		offline: <IoCloudOfflineSharp size={23} />,
	};

	const { mutate, isLoading } = useMutation(createInvoice, {
		onSuccess: ({ message, data }) => {
			showSuccessToast(message);
			navigate(`/invoices/view/${data.uuid}?initiate=1`);
		},
	});

	useEffect(() => {
		return () => removeFromLocalStorage("invoice_items");
	}, []);

	const vatValue = settings?.is_vat_enabled ? settings?.vat / 100 : 0;

	if (!user?.bvn_verified) {
		showInfoToast("Please verify your BVN before proceeding");
		return navigate("/account");
	}

	return (
		<div className="pageWrapper">
			<div className="mainContent">
				<div className="pageTop">
					<button className="mr-2 transparent-btn" onClick={() => navigate("..", { relative: "path" })}>
						<img src={BackIcon} />
					</button>
					<h2>New {getCustomizationValue(user, "invoice_label", true)}</h2>
				</div>

				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={({ invoice_items, ...values }) =>
						mutate({ ...values, invoice_items: invoice_items?.map(({ product, ...rest }) => ({ ...rest })) })
					}
				>
					{({ values, handleChange, handleSubmit, setFieldValue }) => {
						const subTotal = values.invoice_items.reduce((sum, item) => sum + item.product.price * item.qty, 0);
						const discountAmount = subTotal * (Number(values.discount) / 100);
						const vatAmount = Math.max((subTotal - discountAmount) * vatValue, 0);
						const grandTotal = subTotal - discountAmount + vatAmount;

						return (
							<form onSubmit={handleSubmit} className="tw-grid boxed-cont tw-bg-white tw-px-4 md:tw-px-10 tw-py-8 tw-gap-7">
								<div className="tw-flex tw-items-center tw-justify-between tw-flex-wrap tw-gap-x-10 tw-gap-y-4">
									{values.customer ? (
										<div className="tw-flex tw-gap-5 tw-items-center">
											<Button
												variant="outline"
												type="button"
												role="combobox"
												className={cn(
													"tw-border-input tw-min-w-[170px] tw-bg-white tw-px-4 tw-gap-3 tw-py-2.5 tw-text-[15px] tw-font-normal tw-text-gray-500 tw-h-auto",
													"tw-justify-between !tw-min-w-[450px] tw-w-full",
												)}
											>
												{values.customer.first_name} {values.customer.last_name}
											</Button>
											<Button
												variant="hover"
												className="tw-text-red-500"
												onClick={() => {
													setFieldValue("customer", null);
													setFieldValue("customer_id", "");
												}}
											>
												Clear
											</Button>
										</div>
									) : (
										<div className="tw-flex tw-gap-5 tw-items-center tw-flex-wrap">
											<FormLabel name="customer_id">
												<ComboSearch
													value={values.customer_id}
													onChange={(value) => {
														setFieldValue("customer_id", value);
														setFieldValue("group_id", "");
													}}
													placeholder={cn("Search ", getCustomizationValue(user, "customer_label", true), "...")}
													className="tw-justify-between tw-w-full"
													optionRenderer={({ first_name, last_name }) => (
														<div className="flex gap-2">
															<p>
																{first_name} {last_name}
															</p>
														</div>
													)}
													queryKey="customers"
													queryFN={getCustomers}
													mapFN={(option) => ({
														label: `${option.first_name} ${option.last_name}`,
														value: String(option.id),
														info: option,
													})}
												/>
											</FormLabel>

											<p> OR </p>

											<FormLabel name="group_id">
												<ComboSearch
													value={values.group_id}
													onChange={(value) => {
														setFieldValue("group_id", value);
														setFieldValue("customer_id", "");
													}}
													placeholder="Search group..."
													className="tw-justify-between tw-w-full"
													optionRenderer={({ name, description }) => (
														<div className="flex gap-2">
															<p>{name}</p>
															<span className="tw-text-xs tw-bg-slate-300">{description}</span>
														</div>
													)}
													queryKey="group"
													queryFN={getInvoiceGroups}
													mapFN={(option) => ({
														label: `${option.name}`,
														value: String(option.id),
														info: option,
													})}
												/>
											</FormLabel>

											<p> OR </p>

											<CustomModal
												title={cn("Add ", getCustomizationValue(user, "customer_label", true))}
												trigger={
													<button className="blue-btn">Add {getCustomizationValue(user, "customer_label", true)}</button>
												}
											>
												<NewCustomerModal
													onCreate={(customer) => {
														setFieldValue("customer", customer);
														setFieldValue("customer_id", customer?.id);
													}}
												/>
											</CustomModal>
										</div>
									)}

									<FormLabel containerClassName="formItem !tw-m-0 tw-max-w-[200px]" label="Due date" name="due_date">
										<Input name="due_date" type="date" value={values.due_date} onChange={handleChange} />
									</FormLabel>
								</div>

								<div>
									<h2 className="tw-mb-5">{getCustomizationValue(user, "invoice_label", true)} Items</h2>

									<FieldArray name="invoice_items">
										{({ push, replace }) => (
											<div className="tw-flex tw-items-end tw-justify-between tw-gap-4 tw-flex-wrap">
												<FormLabel name="invoice_items" label="Select items from inventory list">
													<ComboSearch
														onChange={(value, option) => {
															if (option.available_qty < 1) {
																showErrorToast("No quantity in stock");
																return;
															}

															const existingItem = values.invoice_items?.find(({ product_id }) => product_id === value);

															if (existingItem) {
																const itemIndex = values.invoice_items?.findIndex(
																	({ product_id }) => product_id === value,
																);
																replace(itemIndex, { ...existingItem, qty: existingItem.qty + 1 });
															} else {
																push({ product_id: value, qty: 1, product: option });
															}
														}}
														placeholder="Search product..."
														className="tw-justify-between tw-max-w-[500px] tw-min-w-[300px] tw-w-full"
														optionRenderer={({ name, available_qty, sku }) => (
															<div>
																<p>
																	{name} (SKU: {sku})
																</p>
																<p className="tw-text-xs">{renderNumber(available_qty)} remaining</p>
															</div>
														)}
														queryKey="items"
														queryFN={getInventory}
														mapFN={(option) => ({
															label: `${option.name} (${option.available_qty})`,
															value: String(option.id),
															info: option,
														})}
													/>
												</FormLabel>
												<CustomModal title="Add Product" trigger={<Button className="!tw-py-2.5">Add Product</Button>}>
													<AddProductModal
														onSubmit={(product) => {
															const existingItem = values.invoice_items?.find(
																({ product_id }) => product_id === product.id,
															);

															if (existingItem) {
																const itemIndex = values.invoice_items?.findIndex(
																	({ product_id }) => product_id === product.id,
																);
																replace(itemIndex, { ...existingItem, qty: existingItem.qty + 1 });
															} else {
																push({ product_id: product.id, qty: 1, product: product });
															}
														}}
													/>
												</CustomModal>
											</div>
										)}
									</FieldArray>
								</div>

								<FieldArray name="invoice_items">
									{({ remove }) => (
										<CustomDataTable
											containerClassName="!tw-bg-transparent !tw-shadow-none"
											columns={columns(setFieldValue, remove)}
											data={values.invoice_items}
											emptyStateMessage={cn("No items added to ", getCustomizationValue(user, "invoice_label", true))}
											pagination={false}
											customStyles={{
												cells: {
													style: {
														"&:not(#cell-5-undefined)": {
															border: "1px solid #CECECE",
															borderRadius: 8,
															paddingBlock: 15,
															paddingInline: 15,
														},
													},
												},
												rows: {
													style: {
														gap: 15,
													},
												},
												headRow: {
													style: {
														gap: 15,
													},
												},
											}}
										/>
									)}
								</FieldArray>

								<div className="">
									<div className="tab-totals">
										<div className="tab-total-lab">
											<p>Subtotal</p>
										</div>
										<div className="tab-total-val">
											<div className="tab-form-elem">
												<span className="tw-ml-auto">₦</span>
												<div className="form-elem-ins">{renderNumber(subTotal)}</div>
											</div>
										</div>
									</div>

									<div className="tab-totals">
										<div className="tab-total-lab">
											<p>Discount</p>
										</div>
										<div className="tab-total-val">
											<div className="tab-form-elem">
												<span className="tw-ml-auto">%</span>
												<input
													type="text"
													name="discount"
													value={values.discount}
													onChange={handleChange}
													className="short_textfield"
												/>
											</div>
										</div>
									</div>

									<div className="tab-totals">
										<div className="tab-total-lab">
											<p>VAT</p>
										</div>
										<div className="tab-total-val">
											<div className="tab-form-elem">
												<span className="tw-ml-auto">₦</span>
												<div className="form-elem-ins">{renderNumber(vatAmount)}</div>
											</div>
										</div>
									</div>

									<div className="tab-totals">
										<div className="tab-total-lab">
											<p>
												<strong>Grand Total</strong>
											</p>
										</div>
										<div className="tab-total-val">
											<p>
												<strong>{renderCurrency(grandTotal)}</strong>
											</p>
										</div>
									</div>
								</div>

								<FormLabel name="payment_method" label="Select Payment Method">
									<div className="radio-tabs-wrap">
										{["transfer", "offline"].map((method) => (
											<div className="radio-tab" key={method}>
												<label
													key={method}
													htmlFor={method}
													className={`radio-tab-label ${values.payment_method === method ? "selected" : ""}`}
												>
													<input
														type="radio"
														name="payment_method"
														id={method}
														value={method}
														checked={values.payment_method === method}
														onChange={handleChange}
														className="hidden"
													/>
													{paymentIcons[method]}
													{method.charAt(0).toUpperCase() + method.slice(1)}
												</label>
											</div>
										))}
									</div>
								</FormLabel>

								<button className="green-btn" disabled={isLoading}>
									Save & Send
								</button>
							</form>
						);
					}}
				</Formik>
			</div>
		</div>
	);
};

export default AddInvoice;
